// ---------------------------------------------------------------------------
// How this player actually plays.
//
// Shared by the account profile and by an AI model's, because the two are the
// same questions asked of the same queries — see `db/subject.ts`. Only the
// sentence under the toggle differs, and it differs because "against people"
// reads differently when the subject is itself a bot.
//
// The same privacy rule as the rest of the profile: render exactly what the
// response carries and add nothing to it.
//
// Three rules about the numbers themselves.
//
// **Never a bare percentage.** Every rate here is shown with the count it came
// out of, because "80% perfect rounds" over five rounds and over five hundred
// are different claims and the reader cannot tell them apart otherwise.
//
// **Never a number with no corpus.** The toggle switches between games somebody
// else human played in and every game, and the line under it always says which
// one is on screen and how many games that is. A stat with an unstated
// denominator is the thing this section exists to avoid.
//
// **Say when a subject was in a lobby twice.** A model can hold two seats in one
// game and then `gamesPlayed` and `seatsHeld` are different numbers, both true.
// The appearances figure appears only when they disagree, because on the
// ordinary profile it would be a second copy of the games count.
// ---------------------------------------------------------------------------

import { useState } from 'react';
import type { DistributionSet, ProfileDistributions } from '@countdown/shared';

import { Histogram } from '../../components/charts/Histogram.js';
import type { Bar } from '../../components/charts/Histogram.js';
import { Card, CardTitle } from '../../components/primitives/Card.js';
import { Segmented } from '../../components/primitives/Segmented.js';

type Corpus = 'human' | 'all';

const OPERATOR_LABEL: Record<string, string> = {
  '+': '+',
  '-': '−',
  '*': '×',
  '/': '÷',
};

const OPERATOR_NAME: Record<string, string> = {
  '+': 'plus',
  '-': 'minus',
  '*': 'times',
  '/': 'divided by',
};

/** `3 of 7` rather than `43%`. A rate with no denominator cannot be read. */
function outOf(part: number, whole: number): string {
  if (whole === 0) return 'no rounds yet';
  return `${part} of ${whole}`;
}

export function Distributions({
  distributions,
  subject = 'account',
}: {
  distributions: DistributionSet;
  /** Which reading of "against people" the caption should give. */
  subject?: 'account' | 'model';
}) {
  const [corpus, setCorpus] = useState<Corpus>('human');
  const shown = corpus === 'human' ? distributions.againstPeople : distributions.allGames;
  const counted =
    corpus === 'human' ? distributions.gamesCounted.againstPeople : distributions.gamesCounted.all;
  const total = distributions.gamesCounted.all;

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-col gap-2">
        <Segmented<Corpus>
          label="which games to count"
          value={corpus}
          onChange={setCorpus}
          options={[
            { value: 'human', label: 'Against people' },
            { value: 'all', label: 'Every game' },
          ]}
        />
        <p className="text-xs text-ink-dim">
          {corpus === 'human'
            ? subject === 'model'
              ? `Games with a person in the room: ${counted} of ${total}. Other AI opponents do not disqualify a game.`
              : `Games with somebody else in them: ${counted} of ${total}. Bots in the room do not disqualify a game.`
            : subject === 'model'
              ? `Every finished game, including AI-only ones: ${counted}.`
              : `Every finished game, solo and against bots included: ${counted}.`}
        </p>
      </div>

      <LettersCard letters={shown.letters} />
      <NumbersCard numbers={shown.numbers} />
      <RestCard shown={shown} />
    </div>
  );
}

function LettersCard({ letters }: { letters: ProfileDistributions['letters'] }) {
  // Every length from three to nine, whether or not it was ever played. A
  // histogram with the empty buckets removed is a different shape from the same
  // data, and the gaps are the interesting part.
  const bars: Bar[] = [];
  for (let length = 3; length <= 9; length += 1) {
    bars.push({
      label: `${length}`,
      longLabel: `${length} letters`,
      value: letters.lengths.find((entry) => entry.length === length)?.count ?? 0,
      tone: length >= 8 ? 'accent' : 'blue',
    });
  }

  return (
    <Card>
      <CardTitle>Letters</CardTitle>

      <div className="mt-4">
        <Histogram
          caption="words by length"
          bars={bars}
          emptyNote="No letters round scored yet."
        />
      </div>

      <dl className="mt-5 grid grid-cols-2 gap-x-4 gap-y-3 sm:grid-cols-3">
        <Figure
          label="average word"
          value={letters.averageLength === null ? null : `${letters.averageLength} letters`}
        />
        <Figure
          label="average board"
          value={letters.averageBest === null ? null : `${letters.averageBest} letters`}
          note="the best that was there"
        />
        <Figure
          label="found the best"
          value={outOf(letters.perfectRounds, letters.roundsPlayed)}
          note="rounds"
        />
        <Figure label="answered" value={outOf(letters.answered, letters.roundsPlayed)} note="rounds" />
        <Figure
          label="thrown out"
          value={
            letters.invalidRate === null
              ? null
              : `${Math.round(letters.invalidRate * 100)}% of answers`
          }
        />
      </dl>
    </Card>
  );
}

function NumbersCard({ numbers }: { numbers: ProfileDistributions['numbers'] }) {
  const bands: Bar[] = [
    { label: 'exact', value: numbers.bands.exact, tone: 'good' },
    { label: '1-5', longLabel: 'within five', value: numbers.bands.within5, tone: 'blue' },
    { label: '6-10', longLabel: 'within ten', value: numbers.bands.within10, tone: 'blue' },
    { label: 'over', longLabel: 'further than ten away', value: numbers.bands.missed, tone: 'alert' },
  ];

  const operators: Bar[] = numbers.operators.map((entry) => ({
    label: OPERATOR_LABEL[entry.op] ?? entry.op,
    longLabel: OPERATOR_NAME[entry.op] ?? entry.op,
    value: entry.count,
    tone: 'blue',
  }));

  return (
    <Card>
      <CardTitle>Numbers</CardTitle>

      <div className="mt-4 flex flex-col gap-5">
        <Histogram
          caption="how close"
          bars={bands}
          emptyNote="No numbers round scored yet."
        />
        <Histogram
          caption="the working"
          bars={operators}
          emptyNote="No working recorded yet."
        />
      </div>

      <dl className="mt-5 grid grid-cols-2 gap-x-4 gap-y-3 sm:grid-cols-3">
        <Figure
          label="average miss"
          value={numbers.averageAway === null ? null : `${numbers.averageAway} away`}
        />
        <Figure
          label="average working"
          value={numbers.averageSteps === null ? null : `${numbers.averageSteps} steps`}
        />
        <Figure
          label="exacts taken"
          value={outOf(numbers.exactsConverted, numbers.exactsAvailable)}
          note="of the rounds one was there"
        />
        <Figure label="answered" value={outOf(numbers.answered, numbers.roundsPlayed)} note="rounds" />
      </dl>
    </Card>
  );
}

function RestCard({ shown }: { shown: ProfileDistributions }) {
  const { conundrum, choosing, shape } = shown;

  return (
    <Card>
      <CardTitle>The rest of it</CardTitle>

      <dl className="mt-4 grid grid-cols-2 gap-x-4 gap-y-3 sm:grid-cols-3">
        <Figure
          label="conundrums solved"
          value={outOf(conundrum.solved, conundrum.roundsPlayed)}
          note="rounds"
        />
        <Figure
          label="buzzed"
          value={outOf(conundrum.buzzed, conundrum.roundsPlayed)}
          note="a wrong buzz locks you out"
        />
        <Figure
          label="points per round"
          value={shape.pointsPerRound === null ? null : `${shape.pointsPerRound}`}
          note="fair across game lengths"
        />
        <Figure
          label="win streak"
          value={`${shape.currentStreak} now, ${shape.longestStreak} best`}
          note={shape.longestStreak >= 8 ? 'an octochamp' : 'the show runs to eight'}
        />
        <Figure
          label="best game"
          value={shape.bestGame === null ? null : `${shape.bestGame.score} points`}
        />
        {/* Only when the two disagree. On an ordinary profile they do not, and
            an "appearances" figure sitting beside an identical games count is
            a number that raises a question instead of answering one. */}
        {shape.seatsHeld > shape.gamesPlayed ? (
          <Figure
            label="appearances"
            value={`${shape.seatsHeld} over ${shape.gamesPlayed} games`}
            note="seated more than once in a lobby"
          />
        ) : null}
        <Figure
          label="letters you called"
          value={
            choosing.lettersChosen === 0
              ? null
              : `${choosing.vowelsCalled} vowels, ${choosing.consonantsCalled} consonants`
          }
          note={choosing.lettersChosen === 0 ? undefined : `over ${choosing.lettersChosen} rounds`}
        />
        <Figure
          label="numbers you called"
          value={
            choosing.numbersChosen === 0
              ? null
              : `${choosing.largeCalled} large, ${choosing.smallCalled} small`
          }
          note={choosing.numbersChosen === 0 ? undefined : `over ${choosing.numbersChosen} rounds`}
        />
      </dl>
    </Card>
  );
}

function Figure({
  label,
  value,
  note,
}: {
  label: string;
  value: string | null;
  note?: string;
}) {
  return (
    <div>
      <dt className="label-mono">{label}</dt>
      <dd className="mt-1 text-[15px] font-semibold">
        {value ?? <span className="font-normal text-ink-dim">Nothing yet.</span>}
      </dd>
      {note && value !== null ? <p className="mt-0.5 text-xs text-ink-dim">{note}</p> : null}
    </div>
  );
}
